// Patent claims exercised: P-002 Claims 9-10 | P-005 Claims 7-8
import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../config';
import MedicalDisclaimer from '../MedicalDisclaimer';
import { handleAIError } from '../../utils/aiErrorHandler';

const SmartShopping = ({ user, token, fetchWithAuth, onClose, onBack }) => {
  // Auth-aware fetch with 401 retry (falls back to raw fetch if fetchWithAuth not provided)
  const authFetch = fetchWithAuth || ((url, opts) => fetch(url, { ...opts, headers: { ...opts?.headers, 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }));
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [shoppingList, setShoppingList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activePlans, setActivePlans] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadActivePlans();
  }, []);

  const loadActivePlans = async () => {
    try {
      const response = await authFetch(`${API_BASE}/api/meal-plans/active`);
      if (response.ok) {
        const data = await response.json();
        setActivePlans(data ? [data] : []);
      }
    } catch (error) {
      console.error('Error loading plans:', error);
    }
  };

  const generateShoppingList = async () => {
    if (!selectedPlan) return;

    setLoading(true);
    setError(null);
    try {
      const response = await authFetch(`${API_BASE}/api/pantry/check-ingredients`, {
        method: 'POST',
        body: JSON.stringify({
          meal_plan_id: selectedPlan
        })
      });

      if (response.ok) {
        const data = await response.json();
        setShoppingList(data);
      } else if (await handleAIError(response)) {
        // unified AI limit modal shown
      } else {
        const errData = await response.json().catch(() => ({}));
        setError(errData.detail || 'Failed to scan pantry');
      }
    } catch (error) {
      console.error('Error:', error);
      setError('Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  if (!shoppingList) {
    const activePlan = activePlans[0];
    return (
      <div className="ss-pageWrapper">
        <div className="ss-cardWrapper">
          <div className="ss-modalHeader">
            <h3 className="ss-pageTitle">Smart Shopping Assistant</h3>
            <button onClick={onBack} className="ss-closeButton">×</button>
          </div>

          <div className="ss-modalBody">
            <label className="ss-promptLabel">
              Scan your pantry against your active meal plan to see what you need to buy.
            </label>

            {activePlan ? (
              <div className="ss-planSelectionContainer">
                <label className="ss-planCheckboxLabel">
                  <input
                    type="checkbox"
                    checked={selectedPlan === 'current'}
                    onChange={(e) => setSelectedPlan(e.target.checked ? 'current' : null)}
                    className="ss-planCheckbox"
                  />
                  {activePlan.plan_name || 'Current Meal Plan'}
                </label>
              </div>
            ) : (
              <div className="ss-emptyPlansMessage">
                No active meal plans found. Generate a meal plan first!
              </div>
            )}

            {error && (
              <div className="ss-errorMessage">{error}</div>
            )}

            <button
              onClick={generateShoppingList}
              disabled={loading || !selectedPlan}
              className={`ss-scanPantryBtn ${loading || !selectedPlan ? 'ss-scanPantryBtn--disabled' : ''}`}
            >
              {loading ? 'Scanning Pantry...' : 'Scan Pantry & Generate List'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ss-pageWrapperList">
      <div className="ss-cardWrapperList">
        <div className="ss-modalHeaderList">
          <h3 className="ss-shoppingListTitle">
            {shoppingList.plan_name ? `${shoppingList.plan_name} — Pantry Check` : 'Pantry Check Results'}
          </h3>
          <button onClick={() => setShoppingList(null)} className="ss-closeButtonList">×</button>
        </div>

        <div className="ss-modalBodyList">
          <div className="ss-summaryBar">
            <span className="ss-summaryItem ss-summaryItem--have">
              {shoppingList.in_pantry_count} in pantry
            </span>
            <span className="ss-summaryItem ss-summaryItem--need">
              {shoppingList.need_to_buy_count} to buy
            </span>
            <span className="ss-summaryItem">
              {shoppingList.total_ingredients} total
            </span>
          </div>

          {shoppingList.in_pantry?.length > 0 && (
            <div className="ss-inPantrySection">
              <strong className="ss-sectionHeading ss-sectionHeading--have">Already in Pantry</strong>
              <ul className="ss-ingredientList">
                {shoppingList.in_pantry.map((item, idx) => (
                  <li key={idx} className="ss-ingredientItem ss-ingredientItem--have">
                    <span className="ss-ingredientCheck">✓</span>
                    <span className="ss-ingredientName">{item.name}</span>
                    {item.quantity > 0 && item.unit && (
                      <span className="ss-ingredientQty">
                        needs {item.quantity} {item.unit}
                        {item.pantry_quantity > 0 && ` (have ${item.pantry_quantity})`}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {shoppingList.need_to_buy?.length > 0 && (
            <div className="ss-needToBuySection">
              <strong className="ss-sectionHeading ss-sectionHeading--need">Need to Buy</strong>
              <ul className="ss-ingredientList">
                {shoppingList.need_to_buy.map((item, idx) => (
                  <li key={idx} className="ss-ingredientItem ss-ingredientItem--need">
                    <span className="ss-ingredientBullet">•</span>
                    <span className="ss-ingredientName">{item.name}</span>
                    {item.quantity > 0 && item.unit && (
                      <span className="ss-ingredientQty">{item.quantity} {item.unit}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {shoppingList.total_ingredients === 0 && (
            <div className="ss-emptyPlansMessage">
              No ingredients found in this meal plan.
            </div>
          )}

          <MedicalDisclaimer variant="health" />

          <div className="ss-exportButtonsContainer">
            <button
              onClick={() => setShoppingList(null)}
              className="ss-backBtn"
            >
              Back
            </button>
            <button
              onClick={onClose}
              className="ss-doneBtn"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmartShopping;
